import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, ListChecks, Printer, Users, XCircle } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { OptionSelect } from '@/components/common/OptionSelect';
import { FeatureStatCard } from '@/components/common/StatCard';
import { buttonVariants } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import {
  downloadPcmpMisStatusReportXlsx,
  pcmpMisReportMembersQueryOptions,
  pcmpMisStatusReportQueryOptions,
} from '../api/pcmpMisStatusReport.api';
import { pcmpStatusCatalogQueryOptions } from '../api/pcmpStatus.api';
import { PcmpCodeSelect } from '../components/PcmpQueue/PcmpCodeSelect';
import { PcmpMisStatusReportTable } from '../components/PcmpReports/PcmpMisStatusReportTable';
import { PcmpExportButton } from '../components/PcmpQueue/PcmpExportButton';
import { PcmpReportFilterBar } from '../components/PcmpReports/PcmpReportFilterBar';
import { usePcmpMisStatusReportFilters } from '../hooks/usePcmpMisStatusReportFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';

/** MIS decisions recorded per team member (LDS-698). */
export function PcmpMisStatusReportPage() {
  const controller = usePcmpMisStatusReportFilters();
  const { params, appliedFilters, misUserId, subStatus, isReady, setMisUserId, setSubStatus } =
    controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpMisStatusReportQueryOptions(params),
    // A reason from a link is checked against the catalog before it is sent.
    enabled: isReady,
  });
  const { data: members, isLoading: membersLoading } = useQuery(pcmpMisReportMembersQueryOptions());
  const { data: catalog, isLoading: catalogLoading } = useQuery(pcmpStatusCatalogQueryOptions());

  const memberOptions = useMemo(
    () =>
      members?.map((member) => ({
        id: member.userId,
        name: member.hrId ? `${member.name} (${member.hrId})` : member.name,
      })),
    [members],
  );

  const noDates = !appliedFilters.dateFrom && !appliedFilters.dateTo;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">MIS Status Update Report</h1>
          <p className="text-sm text-muted-foreground">
            What each MIS team member verified or marked not verified.
          </p>
        </div>
        {canExport ? (
          <div className="flex flex-wrap items-center gap-2">
            <PcmpExportButton
              disabled={!data}
              download={(date) => downloadPcmpMisStatusReportXlsx(params, date)}
            />
            <Link
              to="/pcmp/reports/mis-status/print"
              search={params}
              target="_blank"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
            >
              <Printer className="size-3.5" />
              Print / PDF
            </Link>
          </div>
        ) : null}
      </div>

      <PcmpReportFilterBar controller={controller} idPrefix="pcmp-mis-status" dateSubject="Decided">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pcmp-mis-status-member">MIS member</Label>
          <OptionSelect
            id="pcmp-mis-status-member"
            options={memberOptions}
            isLoading={membersLoading}
            value={misUserId}
            onChange={setMisUserId}
            placeholder="All"
            className="h-10"
            clearable
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pcmp-mis-status-reason">Not Verified reason</Label>
          <PcmpCodeSelect
            id="pcmp-mis-status-reason"
            options={catalog?.misReasons}
            value={subStatus}
            onChange={setSubStatus}
            disabled={catalogLoading}
          />
        </div>
      </PcmpReportFilterBar>

      <p className="text-xs text-muted-foreground">
        {noDates
          ? "With no dates the report shows today's decisions. "
          : 'Dates are the day of the decision. '}
        One update is one decision, not one proposal.
        {subStatus ? ' A reason narrows the Not Verified side, so Verified shows 0.' : ''}
      </p>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <FeatureStatCard
          icon={CheckCircle2}
          accent="emerald"
          label="Verified"
          value={data?.verifiedTotal}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={XCircle}
          accent="rose"
          label="Not Verified"
          value={data?.notVerifiedTotal}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={ListChecks}
          accent="blue"
          label="Total Updates"
          value={data?.totalUpdates}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={Users}
          accent="indigo"
          label="Team Members"
          value={data?.memberCount}
          isLoading={isLoading}
          isError={!!error}
        />
      </div>

      {error && !data ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the MIS Status report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="font-medium">{data?.projectLabel ?? 'Loading…'}</span>
            <span className="text-muted-foreground">{data?.periodLabel}</span>
          </div>
          {data ? (
            <div
              className={cn('overflow-x-auto', isFetching && 'opacity-60 transition-opacity')}
              aria-busy={isFetching}
            >
              <PcmpMisStatusReportTable report={data} />
            </div>
          ) : (
            <div className="h-48 animate-pulse rounded-md bg-muted" aria-hidden />
          )}
        </div>
      )}
    </div>
  );
}
